import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getCookie } from "../cookie_helper/cookie";

export const monitoringApi = createApi({
  reducerPath: "monitoringApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_URL,
    prepareHeaders: (headers) => {
      const token = getCookie("token");
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  tagTypes: ["Alerts"],
  endpoints: (builder) => ({
    getLive: builder.query({ query: () => `monitoring/live` }),
    // Who is online right now, by name — users and listeners, with the
    // socket-verified presence flag (live vs ghost).
    getOnlineNow: builder.query({
      query: ({ role = "all", limit = 200 } = {}) =>
        `monitoring/online-now?role=${role}&limit=${limit}`,
    }),
    // HR view of call rejections: per-listener scorecard + "where to focus".
    getRejectionFocus: builder.query({
      query: (days = 7) => `monitoring/rejection-focus?days=${days}`,
    }),
    // Ghost controls. sweepGhosts is a DRY RUN unless apply:true is sent.
    forceOffline: builder.mutation({
      query: ({ userId, notify = true }) => ({
        url: `monitoring/force-offline`,
        method: "POST",
        body: { userId, notify },
      }),
    }),
    wakeListener: builder.mutation({
      query: ({ userId }) => ({
        url: `monitoring/wake`,
        method: "POST",
        body: { userId },
      }),
    }),
    sweepGhosts: builder.mutation({
      query: ({ minAgeMin = 45, apply = false, notify = true } = {}) => ({
        url: `monitoring/sweep-ghosts`,
        method: "POST",
        body: { minAgeMin, apply, notify },
      }),
    }),
    getListeners: builder.query({ query: (days = 7) => `monitoring/listeners?days=${days}` }),
    getBillingIntegrity: builder.query({ query: (days = 7) => `monitoring/billing-integrity?days=${days}` }),
    getCallHealth: builder.query({ query: (days = 7) => `monitoring/call-health?days=${days}` }),
    getCallQuality: builder.query({ query: (days = 7) => `call-metrics/admin/summary?days=${days}` }),
    getCallQualityList: builder.query({
      query: ({ days = 7, filter = "poor", page = 1 } = {}) =>
        `call-metrics/admin/list?days=${days}&filter=${filter}&page=${page}`,
    }),
    getSessionDetail: builder.query({ query: (id) => `monitoring/session/${id}` }),
    // Chat transcript for one session. Backend is admin-only and additionally
    // gated by CHAT_TRANSCRIPT_ENABLED, which comes back as a 403 with
    // { disabled: true } so the UI can explain rather than just fail.
    getSessionTranscript: builder.query({
      query: ({ id, page = 1, limit = 200 }) =>
        `monitoring/session/${id}/transcript?page=${page}&limit=${limit}`,
    }),
    // Call recording playback for one session. Returns a SHORT-LIVED presigned
    // URL (default 5 min), so this must not be cached or prefetched — refetch on
    // every open, or the <audio> src will 403 after expiry.
    // 403 = CALL_RECORDING_ENABLED off, 404 = no recording yet, 410 = purged.
    getSessionRecording: builder.query({
      // arg is a session id, or { id, download: true } to mint a file-save URL.
      query: (arg) => {
        const id = typeof arg === "object" ? arg.id : arg;
        const dl = typeof arg === "object" && arg.download ? "?download=1" : "";
        return `monitoring/session/${id}/recording${dl}`;
      },
      keepUnusedDataFor: 0,
    }),
    getAlerts: builder.query({
      query: ({ resolved = "false", page = 1 } = {}) => `monitoring/alerts?resolved=${resolved}&page=${page}`,
      providesTags: ["Alerts"],
    }),
    scanAlerts: builder.mutation({
      query: (days = 2) => ({ url: `monitoring/alerts/scan?days=${days}`, method: "POST" }),
      invalidatesTags: ["Alerts"],
    }),
    resolveAlert: builder.mutation({
      query: (id) => ({ url: `monitoring/alerts/${id}/resolve`, method: "POST" }),
      invalidatesTags: ["Alerts"],
    }),
  }),
});

export const {
  useGetLiveQuery,
  useGetOnlineNowQuery,
  useGetRejectionFocusQuery,
  useForceOfflineMutation,
  useWakeListenerMutation,
  useSweepGhostsMutation,
  useGetListenersQuery,
  useGetBillingIntegrityQuery,
  useGetCallHealthQuery,
  useGetCallQualityQuery,
  useGetCallQualityListQuery,
  useGetSessionDetailQuery,
  useGetSessionTranscriptQuery,
  useLazyGetSessionRecordingQuery,
  useGetSessionRecordingQuery,
  useGetAlertsQuery,
  useScanAlertsMutation,
  useResolveAlertMutation,
} = monitoringApi;
